import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTRPC } from './trpc'
import { getSessionToken, setSessionToken, subscribeSession } from './session'
import type { Me } from './api-types'

type AuthContextValue = {
  token: string | null
  /** Starts a session; the user from the sign-in response seeds the cache so nothing flashes. */
  signIn: (token: string, user?: Me) => void
  signOut: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const token = useSyncExternalStore(subscribeSession, getSessionToken)
  const queryClient = useQueryClient()
  const trpc = useTRPC()
  const signIn = useCallback(
    (next: string, user?: Me) => {
      setSessionToken(next)
      if (user) queryClient.setQueryData(trpc.auth.me.queryKey(), user)
    },
    [queryClient, trpc],
  )
  const signOut = useCallback(() => {
    setSessionToken(null)
    queryClient.clear()
  }, [queryClient])
  const value = useMemo(() => ({ token, signIn, signOut }), [token, signIn, signOut])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth outside AuthProvider')
  return ctx
}

/** The signed-in user, null when signed out, undefined while loading. */
export function useMe() {
  const trpc = useTRPC()
  const { token } = useAuth()
  const query = useQuery({
    ...trpc.auth.me.queryOptions(),
    enabled: token !== null,
    retry: false,
    staleTime: 30_000,
  })
  if (token === null) return { user: null as Me | null, isLoading: false, refetch: query.refetch }
  return {
    user: query.data ?? (query.isError ? null : undefined),
    isLoading: query.isPending,
    refetch: query.refetch,
  }
}
